import React from "react";
import { Box, Typography, Card, CardContent, CircularProgress } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import YearlyInvoiceTrendChart from "./YearlyInvoiceTrendChart";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const s = String(dateString);
    if (s.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = s.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

// Format currency: full number below 1M, compact (1M, 1.5B) only for 1M and above — exported for use in CustomerInvoices
export const formatCurrency = (value) => {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  if (Math.abs(num) >= 1_000_000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(num);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Four metric boxes for Customer Invoices page: Total Invoices, Total Billed,
 * Latest Invoice Amount, and Yearly Invoice Trend chart.
 * Customer-scope API (scope=customer, customer_orgcode required).
 */
const CustomerInvoicesMetrics = ({
  customerName,
  totalInvoices,
  totalBilled,
  latestInvoiceAmount,
  lastInvoicePeriodStart,
  customerMetrics,
  isFetchingCustomerMetrics,
  customerMetricsError,
}) => {
  return (
    <>
      {customerMetricsError && (
        <Box sx={{ p: 1.5, mb: 1.5, borderRadius: "6px", bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
          <Typography sx={{ fontSize: "0.8125rem", color: "#b91c1c" }}>
            {typeof customerMetricsError === "string" ? customerMetricsError : (getApiErrorMessage(customerMetricsError) || "Failed to load metrics.")}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 1.5, mb: 2 }}>
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            height: 100,
            "&:hover": { boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.08)" },
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography sx={{ color: "#64748b", fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
              Total Invoices Approved & Sent <Box component="span" sx={{ fontWeight: 500, opacity: 0.85, textTransform: "none" }}>(YTD)</Box>
            </Typography>
            {isFetchingCustomerMetrics ? (
              <CircularProgress size={24} sx={{ color: "#3b82f6", mt: 0.5 }} />
            ) : (
              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.25rem", lineHeight: 1.2 }}>
                {totalInvoices != null ? totalInvoices : "—"}
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            height: 100,
            "&:hover": { boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.08)" },
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography sx={{ color: "#64748b", fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
              Total Billed <Box component="span" sx={{ fontWeight: 500, opacity: 0.85, textTransform: "none" }}>(YTD)</Box>
            </Typography>
            {isFetchingCustomerMetrics ? (
              <CircularProgress size={24} sx={{ color: "#3b82f6", mt: 0.5 }} />
            ) : (
              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.25rem", lineHeight: 1.2 }}>
                {formatCurrency(totalBilled)}
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            height: 100,
            "&:hover": { boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.08)" },
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography sx={{ color: "#64748b", fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
              Latest Invoice Amount
            </Typography>
            {isFetchingCustomerMetrics ? (
              <CircularProgress size={24} sx={{ color: "#3b82f6", mt: 0.5 }} />
            ) : (
              <>
                <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.25rem", lineHeight: 1.2 }}>
                  {formatCurrency(latestInvoiceAmount)}
                </Typography>
                {lastInvoicePeriodStart && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.25 }}>
                    <CalendarMonthIcon sx={{ fontSize: 12, color: "#64748b" }} />
                    <Typography sx={{ fontSize: "0.6875rem", color: "#64748b" }}>
                      {formatDate(lastInvoicePeriodStart)}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <YearlyInvoiceTrendChart
          trendData={customerMetrics?.yearly_invoice_trend}
          isLoading={isFetchingCustomerMetrics}
        />
      </Box>
    </>
  );
};

export default CustomerInvoicesMetrics;
